// Berichte (Runde 7, Agent 7A): Typen und Client nach crm/API.md, Abschnitt 22.1. Vorarbeit: die Formen stehen, die Routen
// antworten bis zum Bau von 7A mit leeren Listen bzw. 501 `nicht_umgesetzt`. 7A erweitert diese Datei nur additiv.
import { http } from "./client";

export type KennzahlEinheit = "anzahl" | "sekunden" | "euro" | "prozent";
export type Vergleich = "vorperiode" | "vorjahr";
export type Gruppierung = "keine" | "tag" | "woche" | "monat" | "nutzer";
export type BerichtArt = "uebersicht" | "vergleich" | "trichter" | "anrufe" | "statuswechsel" | "gesendet";

export interface Kennzahl {
  id: string;
  gruppe: string;
  titel: string;
  /** graue Zeile unter der Zahl, Großbuchstaben (z. B. "ALLE ARTEN") */
  untertitel?: string;
  einheit: KennzahlEinheit;
  beschreibung?: string;
}

/** Eingabe aller Berichtsabfragen: Tage in Europe/Berlin (JJJJ-MM-TT), `bis` einschließlich */
export interface BerichtEingabe {
  von: string;
  bis: string;
  /** leer = alle, die der Nutzer sehen darf (Recht `berichte_alle`) */
  nutzer_ids?: string[];
  /** nur Leads dieser Smart View */
  smart_view_id?: string;
  vergleich?: Vergleich;
}

export interface AbfrageEingabe extends BerichtEingabe {
  kennzahlen: string[];
  gruppieren?: Gruppierung;
}

export interface KennzahlWert {
  kennzahl: string;
  wert: number | null;
  vergleich_wert?: number | null;
  veraenderung_prozent?: number | null;
  /** bei gruppieren=tag|woche|monat: Verlauf */
  reihe?: { zeit: string; wert: number | null }[];
}

export interface BerichtGruppe {
  /** Nutzer-ID bzw. Zeitraumbeginn */
  schluessel: string;
  titel: string;
  werte: Record<string, number | null>;
}

export interface AbfrageAntwort {
  von: string;
  bis: string;
  vergleich_von?: string | null;
  vergleich_bis?: string | null;
  data: KennzahlWert[];
  /** bei gruppieren=nutzer: Tabelle Nutzer mal Kennzahl */
  gruppen?: BerichtGruppe[];
  summe?: Record<string, number | null>;
  /** true: ohne das Recht `berichte_alle` zählen nur die eigenen Zahlen */
  eingeschraenkt?: boolean;
}

export interface AnrufeAntwort {
  von: string;
  bis: string;
  gesamt: number;
  vergleich_gesamt?: number | null;
  reihe: { zeit: string; anzahl: number }[];
  nach_dauer: { klasse: string; anzahl: number }[];
  nach_ergebnis: { ergebnis: string; schluessel?: string; anzahl: number }[];
  eindeutige_leads: number;
  dauer_gesamt_s?: number;
  dauer_schnitt_s?: number | null;
  verlauf_art?: "tag" | "woche" | "monat";
  vergleich_dauer_gesamt_s?: number;
  vergleich_eindeutige_leads?: number;
  veraenderung_prozent?: number | null;
  eingeschraenkt?: boolean;
}

export interface StatuswechselZeile {
  status_id: string;
  label: string;
  farbe?: string | null;
  betreten: number;
  verlassen: number;
  /** Bestand am Anfang und Ende; leer, wenn nach Nutzer gefiltert wird (ein Bestand gehört keinem Nutzer) */
  start: number | null;
  ende: number | null;
  netto: number;
  netto_prozent?: number | null;
  nach_nutzer?: { nutzer_id: string; titel: string; betreten: number; verlassen: number }[];
}

export interface StatuswechselAntwort {
  data: StatuswechselZeile[];
  summe?: { betreten: number; verlassen: number; start: number; ende: number };
  bestand_bekannt?: boolean;
  eingeschraenkt?: boolean;
}

export interface TrichterAntwort {
  karten: Record<string, number | null>;
  stufen: { status_id: string; label: string; typ: string; anzahl: number; betrag: number; erreicht?: number }[];
  pipeline_id?: string | null;
  pipeline_name?: string;
  hinweis?: string;
  eingeschraenkt?: boolean;
}

export interface GesendetZeile {
  vorlage_id: string | null;
  name: string;
  gesendet: number;
  beantwortet: number;
  antwortquote: number | null;
}

export interface GesendetAntwort {
  data: GesendetZeile[];
  summe?: { gesendet: number; beantwortet: number; antwortquote: number | null };
  hinweis?: string;
  eingeschraenkt?: boolean;
}

export interface GespeicherterBericht {
  id: string;
  name: string;
  art: BerichtArt;
  konfiguration: Record<string, unknown>;
  besitzer_id: string | null;
  besitzer_name?: string | null;
  geteilt: boolean;
  ist_standard: boolean;
  /** true, wenn der angemeldete Nutzer ihn ändern darf (Besitzer) */
  darf_aendern?: boolean;
  date_created: string | null;
  date_updated: string | null;
}

const k = encodeURIComponent;

export const berichteApi = {
  kennzahlen: () => http.get<{ data: Kennzahl[] }>("/api/berichte/kennzahlen"),
  abfrage: (e: AbfrageEingabe) => http.post<AbfrageAntwort>("/api/berichte/abfrage", e),
  anrufe: (e: BerichtEingabe & { richtung?: "alle" | "ausgehend" | "eingehend" }) => http.post<AnrufeAntwort>("/api/berichte/anrufe", e),
  statuswechsel: (e: BerichtEingabe) => http.post<StatuswechselAntwort>("/api/berichte/statuswechsel", e),
  trichter: (e: BerichtEingabe & { pipeline_id?: string; wert?: "anzahl" | "betrag" }) => http.post<TrichterAntwort>("/api/berichte/trichter", e),
  gesendet: (e: BerichtEingabe) => http.post<GesendetAntwort>("/api/berichte/gesendet", e),
  /** CSV als Text; die Seite macht daraus den Download */
  export: async (art: "abfrage" | "anrufe" | "statuswechsel" | "trichter" | "gesendet", parameter: Record<string, unknown>) => {
    const antwort = await fetch("/api/berichte/export", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ art, parameter }),
    });
    const text = await antwort.text();
    if (!antwort.ok) {
      let meldung = `Export fehlgeschlagen (${antwort.status}).`;
      try {
        meldung = (JSON.parse(text) as { error?: { message?: string } }).error?.message ?? meldung;
      } catch {
        /* keine JSON-Antwort: allgemeine Meldung */
      }
      throw new Error(meldung);
    }
    return text;
  },
  gespeicherte: () => http.get<{ data: GespeicherterBericht[] }>("/api/berichte/gespeichert"),
  speichern: (b: { name: string; art?: BerichtArt; konfiguration?: Record<string, unknown>; geteilt?: boolean }) =>
    http.post<{ bericht: GespeicherterBericht }>("/api/berichte/gespeichert", b),
  holen: (id: string) => http.get<{ bericht: GespeicherterBericht }>(`/api/berichte/gespeichert/${k(id)}`),
  aendern: (id: string, b: { name?: string; konfiguration?: Record<string, unknown>; geteilt?: boolean }) =>
    http.patch<{ bericht: GespeicherterBericht }>(`/api/berichte/gespeichert/${k(id)}`, b),
  loeschen: (id: string) => http.del<void>(`/api/berichte/gespeichert/${k(id)}`),
  kopieren: (id: string) => http.post<{ bericht: GespeicherterBericht }>(`/api/berichte/gespeichert/${k(id)}/kopieren`),
};
